'use client';
import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import { TrendingUp, BookOpen, ShieldCheck, Globe, Package, BarChart2, Eye, Users, DollarSign, FileText } from 'lucide-react';
import { toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';


const kpis = [
  { label: 'Investment Opportunities', value: '246', sub: '+12 this week', color: 'var(--gold)', icon: TrendingUp },
  { label: 'Due Diligence Ready', value: '38', sub: 'Full packs available', color: 'var(--emerald-bright)', icon: BookOpen },
  { label: 'Shariah Reviewed Deals', value: '72', sub: 'Fully compliant', color: '#A855F7', icon: ShieldCheck },
  { label: 'Total Funding Demand', value: 'US$184M', sub: 'Across all sectors', color: 'var(--info)', icon: DollarSign },
  { label: 'Investor Packs Requested', value: '11', sub: 'By your account', color: 'var(--warning)', icon: FileText },
  { label: 'High Trust Opportunities', value: '29', sub: 'Score ≥ 85%', color: 'var(--danger)', icon: Eye },
];

const actions = [
  { label: 'View Investment Exchange', icon: TrendingUp, color: 'var(--gold)' },
  { label: 'Open Due Diligence Room', icon: BookOpen, color: 'var(--emerald-bright)' },
  { label: 'Request Investor Pack', icon: Package, color: '#A855F7' },
  { label: 'Compare Opportunities', icon: BarChart2, color: 'var(--info)' },
  { label: 'View Trade Intelligence', icon: Globe, color: 'var(--warning)' },
  { label: 'Schedule Founder Meeting', icon: Users, color: 'var(--danger)' },
];

const opportunities = [
  { name: 'Halal Food Manufacturing Expansion', country: 'Malaysia', funding: 'US$2.5M', stage: 'Series A', sector: 'Halal Food', shariah: 'Completed', dd: 88, trust: 94 },
  { name: 'Ethical Halal Cosmetics Brand', country: 'United States', funding: 'US$1.2M', stage: 'Seed', sector: 'Cosmetics', shariah: 'In Progress', dd: 81, trust: 88 },
  { name: 'China Halal Export Onboarding', country: 'China', funding: 'US$4.0M', stage: 'Strategic', sector: 'Manufacturing', shariah: 'Completed', dd: 84, trust: 85 },
  { name: 'OIC Food Security Agriculture', country: 'Indonesia', funding: 'US$7.5M', stage: 'Infrastructure', sector: 'Agriculture', shariah: 'Completed', dd: 90, trust: 92 },
];

const corridors = [
  { name: 'Malaysia', role: 'Trust Anchor', color: 'var(--emerald-bright)', deals: 89 },
  { name: 'China', role: 'Manufacturing & Export Scale', color: 'var(--info)', deals: 64 },
  { name: 'United States', role: 'Investment & Innovation', color: 'var(--gold)', deals: 41 },
  { name: 'OIC Markets', role: 'Long-Term Expansion Base', color: '#A855F7', deals: 52 },
];

const activities = [
  { text: 'Investor pack requested for Halal Food Manufacturing Expansion.', time: '10 min ago', type: 'success' },
  { text: 'Shariah review completed for OIC Agriculture Project.', time: '45 min ago', type: 'info' },
  { text: 'Certificate verified for Nusantara Halal Foods.', time: '2 hr ago', type: 'success' },
  { text: 'New US halal cosmetics opportunity added.', time: '5 hr ago', type: 'info' },
  { text: 'Crescent Capital Partners viewed Malaysia-China corridor report.', time: '1 day ago', type: 'info' },
];

const nextSteps = [
  'Review Halal Food Manufacturing Expansion',
  'Request investor pack from Global Tayyib Foods',
  'Check certificate validity before investment review',
  'Compare Malaysia vs Indonesia agriculture opportunities',
  'Schedule meeting with SME founder',
];

const sectors = [
  { name: 'Halal Food', pct: 34, color: 'var(--emerald-bright)' },
  { name: 'Cosmetics', pct: 18, color: '#A855F7' },
  { name: 'Pharmaceuticals', pct: 14, color: 'var(--info)' },
  { name: 'Agriculture', pct: 16, color: 'var(--gold)' },
  { name: 'Modest Fashion', pct: 8, color: 'var(--warning)' },
  { name: 'Islamic Finance', pct: 10, color: 'var(--danger)' },
];

export default function InvestorDashboardPage() {
  const [activeOpp, setActiveOpp] = useState<string | null>(null);

  return (
    <AppLayout activeRoute="/dashboard/investor">
      <div className="space-y-6 max-w-screen-2xl">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Investor Dashboard</h1>
            <p className="text-sm mt-1" style={{ color: 'var(--muted-foreground)' }}>
              Explore verified halal and Shariah-aligned investment opportunities with due diligence, certificate confidence, and market corridor intelligence.
            </p>
          </div>
          <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium shrink-0"
            style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.25)', color: 'var(--gold)' }}>
            <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: 'var(--gold)' }} />
            US$184M Pipeline
          </div>
        </div>

        {/* KPI Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
          {kpis?.map((k) => {
            const Icon = k?.icon;
            return (
              <div key={k?.label} className="rounded-xl p-4" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
                <Icon size={16} className="mb-2" style={{ color: k?.color }} />
                <p className="text-xl font-extrabold font-mono-data" style={{ color: k?.color }}>{k?.value}</p>
                <p className="text-xs font-semibold mt-0.5">{k?.label}</p>
                <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>{k?.sub}</p>
              </div>
            );
          })}
        </div>

        {/* Actions */}
        <div className="rounded-xl p-5" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
          <h2 className="text-sm font-semibold mb-4">Investor Actions</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3">
            {actions?.map((a) => {
              const Icon = a?.icon;
              return (
                <button key={a?.label} onClick={() => toast?.success(`${a?.label} — opening module`)}
                  className="flex flex-col items-center gap-2 p-3 rounded-xl text-center transition-all duration-200 hover:-translate-y-0.5"
                  style={{ background: `${a?.color}10`, border: `1px solid ${a?.color}25` }}>
                  <Icon size={20} style={{ color: a?.color }} />
                  <span className="text-xs font-medium leading-tight">{a?.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Main Grid */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <div className="xl:col-span-2 space-y-5">
            {/* Featured Opportunities */}
            <div className="rounded-xl p-5" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
              <h3 className="text-sm font-semibold mb-4">Featured Investment Opportunities</h3>
              <div className="space-y-3">
                {opportunities?.map((o) => (
                  <div key={o?.name}
                    className="p-4 rounded-xl cursor-pointer transition-all duration-200"
                    style={{ background: activeOpp === o?.name ? 'rgba(245,158,11,0.06)' : 'var(--muted)', border: `1px solid ${activeOpp === o?.name ? 'rgba(245,158,11,0.3)' : 'var(--border)'}` }}
                    onClick={() => setActiveOpp(activeOpp === o?.name ? null : o?.name)}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold">{o?.name}</p>
                        <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>{o?.country} · {o?.sector} · {o?.stage}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-sm font-bold font-mono-data" style={{ color: 'var(--gold)' }}>{o?.funding}</p>
                        <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>DD: {o?.dd}%</p>
                      </div>
                    </div>
                    {activeOpp === o?.name && (
                      <div className="mt-3 pt-3 border-t flex items-center justify-between" style={{ borderColor: 'var(--border)' }}>
                        <div className="flex items-center gap-3">
                          <span className="text-xs px-2 py-0.5 rounded-full"
                            style={{ background: o?.shariah === 'Completed' ? 'rgba(16,185,129,0.15)' : 'rgba(245,158,11,0.15)', color: o?.shariah === 'Completed' ? 'var(--emerald-bright)' : 'var(--warning)' }}>
                            Shariah: {o?.shariah}
                          </span>
                          <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Trust: {o?.trust}%</span>
                        </div>
                        <button onClick={(e) => { e?.stopPropagation(); toast?.success('Investor pack requested!'); }}
                          className="text-xs px-3 py-1.5 rounded-lg font-semibold"
                          style={{ background: 'rgba(245,158,11,0.2)', color: 'var(--gold)' }}>
                          Request Pack
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Corridor Intelligence */}
            <div className="rounded-xl p-5" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
              <h3 className="text-sm font-semibold mb-4">Corridor Intelligence</h3>
              <div className="grid grid-cols-2 gap-3">
                {corridors?.map((c) => (
                  <div key={c?.name} className="p-4 rounded-xl" style={{ background: 'var(--muted)', border: `1px solid ${c?.color}25` }}>
                    <p className="text-sm font-bold" style={{ color: c?.color }}>{c?.name}</p>
                    <p className="text-xs mt-1" style={{ color: 'var(--muted-foreground)' }}>{c?.role}</p>
                    <p className="text-xs font-mono-data mt-2 font-semibold">{c?.deals} active deals</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-5">
            {/* Sector Map */}
            <div className="rounded-xl p-5" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
              <h3 className="text-sm font-semibold mb-4">Sector Opportunity Map</h3>
              <div className="space-y-2">
                {sectors?.map((s) => (
                  <div key={s?.name}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs">{s?.name}</span>
                      <span className="text-xs font-mono-data font-semibold" style={{ color: s?.color }}>{s?.pct}%</span>
                    </div>
                    <div className="w-full rounded-full h-1.5" style={{ background: 'var(--border)' }}>
                      <div className="h-1.5 rounded-full" style={{ width: `${s?.pct}%`, background: s?.color }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-xl p-5" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
              <h3 className="text-sm font-semibold mb-4">Recent Activity</h3>
              <div className="space-y-3">
                {activities?.map((a, i) => (
                  <div key={i} className="flex gap-3">
                    <div className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0" style={{ background: a?.type === 'success' ? 'var(--emerald-bright)' : 'var(--gold)' }} />
                    <div>
                      <p className="text-xs leading-relaxed">{a?.text}</p>
                      <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>{a?.time}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-xl p-5" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
              <h3 className="text-sm font-semibold mb-3">Recommended Next Steps</h3>
              <div className="space-y-2">
                {nextSteps?.map((step, i) => (
                  <div key={i} className="flex items-start gap-2 p-2 rounded-lg cursor-pointer transition-colors hover:bg-muted"
                    onClick={() => toast?.success('Opening task...')}>
                    <span className="text-xs font-bold mt-0.5 shrink-0" style={{ color: 'var(--gold)' }}>{i + 1}.</span>
                    <p className="text-xs leading-relaxed">{step}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
